import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2, XCircle } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { subscriptionService, type SubscriptionListItem } from '@/services/subscription.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues { reason: string }

interface Props {
  subscription: SubscriptionListItem | null;
  onClose: () => void;
}

export function CancelDialog({ subscription, onClose }: Props) {
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { reason: '' },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      subscriptionService.cancel(subscription!.id, { reason: v.reason.trim() }),
    onSuccess: () => {
      toast.success('Subscription cancelled');
      void queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      reset();
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={!!subscription} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <XCircle className="h-4 w-4" />
            Cancel Subscription
          </DialogTitle>
        </DialogHeader>
        {subscription && (
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <div className="rounded-lg bg-destructive/5 border border-destructive/20 px-4 py-3 text-sm">
              <p className="font-medium">{subscription.name}</p>
              {subscription.vendor_name && (
                <p className="text-muted-foreground">{subscription.vendor_name}</p>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              This will mark the subscription as <strong>Cancelled</strong>. This action cannot be undone without creating a new subscription.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="cancel-reason">Reason *</Label>
              <Textarea
                id="cancel-reason"
                {...register('reason', { required: 'Please provide a reason for cancellation' })}
                placeholder="Why is this subscription being cancelled?"
                rows={3}
              />
              {errors.reason && <p className="text-xs text-destructive">{errors.reason.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }}>Keep Subscription</Button>
              <Button type="submit" variant="destructive" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Cancel Subscription
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
